import Vue from 'vue'
import { getQueryStringValue } from '@/utils/querystring'
const shouldLog =
  (getQueryStringValue('verbose') || '').includes('1') ||
  (getQueryStringValue('verbose') || '').includes('layout')

function getResizeableDefaults() {
  return {
    isResizeable: false,
    resizeInProgress: false,
    toggleInProgress: false,
  }
}

function getLayoutDefaults() {
  return {
    currentFloatingModule: '',
    currentLayoutName: '',
    components: {
      menu: null,
      rightMenuTopComponent: null,
    },
    resizedIndex: 0,
    isSidebarVisible: false,
    isSidebarExtended: false,
    isMenuVisible: false,
    isMenuCollapsed: false,
    isMenuToggleVisible: true,
    willMenuCollapseCompletely: true,
    isWideMenuVisible: false,
    isSubMenuVisible: false,
    isRightMenuVisible: false,
    isRightMenuBottomVisible: false,
    isRightMenuBottomCollapsed: false,
    isRightMenuBottomExpanded: false,
    rightMenuBottomCurrentHeight: 400,
    //right menu bottom layout
    getRightMenuBottomComponent() {
      if (this.isRightMenuBottomCollapsed) {
        return {
          rightMenuBottomPaddingBottom: 0,
          mainTableDivDisplay: 'none',
          rightMenuBottomHeight: 25,
          resizeableDirectiveHorizontalToggleButtonIcon: 'fas fa-chevron-up',
        }
      } else if (this.isRightMenuBottomExpanded) {
        return {
          rightMenuBottomPaddingBottom: 5,
          mainTableDivDisplay: 'block',
          rightMenuBottomHeight: window.innerHeight, // 100vh
          resizeableDirectiveHorizontalToggleButtonIcon: 'fas fa-compress',
        }
      } else {
        return {
          rightMenuBottomPaddingBottom: 5,
          mainTableDivDisplay: 'block',
          rightMenuBottomHeight: 400,
          resizeableDirectiveHorizontalToggleButtonIcon: 'fas fa-chevron-down',
        }
      }
    },
  }
}

/**
 * @namespace Stores
 * @category Stores
 * @module app-store
 * @todo Refactor/Move layout somewhere else (Layout store)
 * @todo Refactor/Remove layoutType
 * */
export default {
  namespaced: true,
  state: {
    currentFloatingModule: '',
    layoutType: 'none', //['none','tlayout']
    layout: {
      ...getLayoutDefaults(),
    },
    resizeable: {
      ...getResizeableDefaults(),
    },
  },
  getters: {
    resizeInProgress(state) {
      return state.resizeable.resizeInProgress
    },
    isResizeable(state) {
      return state.resizeable.isResizeable
    },
    layout(state) {
      return state.layout
    },
    isSidebarExtended(state) {
      return state.layout.isSidebarExtended
    },
    layoutComponent(state) {
      return (name) => {
        return (
          state.layout.components[name] ||
          (Vue.$layout.components && Vue.$layout.components[name])
        )
      }
    },
    currentFloatingModule(state) {
      return state.currentFloatingModule
    },
    toggleInProgress(state) {
      return state.resizeable.resizeInProgress
    },
  },
  mutations: {
    updateLayout(state, options = {}) {
      state.layout = {
        ...state.layout,
        ...options,
      }
    },
    updateLayoutComponents(state, componentNames) {
      state.layout = {
        ...state.layout,
        components: {
          ...state.layout.components,
          ...componentNames,
        },
      }
    },
    resetState(state) {
      state.layout = getLayoutDefaults()
      state.currentFloatingModule = ''
    },
    setCurrentFloatingModule(state, moduleName) {
      state.currentFloatingModule = moduleName
    },
    updateIsLayoutTableMapResizeable(state, resizeable) {
      state.resizeable.isResizeable = resizeable
    },
    updateIsLayoutTableMapResizeInProgress(state, resizeable) {
      state.resizeable.resizeInProgress = resizeable
    },
    updateIsRightMenuBottomCollapsed(state, isCollapsed) {
      state.layout.isRightMenuBottomCollapsed = isCollapsed
    },
    updateIsLayoutTableMapToggleInProgress(state, resizeable) {
      state.resizeable.toggleInProgress = resizeable
    },
    setRightMenuBottomCurrentHeight(state, height) {
      state.layout.rightMenuBottomCurrentHeight = height
    },
  },
  actions: {
    setRightMenuBottomState({ state }, stateName) {
      switch (stateName) {
        case 'visible-default':
          state.layout.isRightMenuBottomCollapsed = false
          state.layout.isRightMenuBottomExpanded = false
          break
        case 'visible-expanded':
          state.layout.isRightMenuBottomCollapsed = false
          state.layout.isRightMenuBottomExpanded = true
          break
        case 'hidden':
          state.layout.isRightMenuBottomCollapsed = true
          state.layout.isRightMenuBottomExpanded = false
          break
      }
    },
    setCurrentFloatingModule({ commit }, moduleName) {
      commit('setCurrentFloatingModule', moduleName)
    },
    updateLayout({ commit }, options) {
      commit('updateLayout', options)
    },
    setIsLayoutTableMapResizeable({ commit }, resizeable) {
      commit('updateIsLayoutTableMapResizeable', resizeable)
    },
    setIsLayoutResizeInProgress({ commit }, inProgress) {
      commit('updateIsLayoutTableMapResizeInProgress', inProgress)
    },
    setIsRightMenuBottomCollapsed({ commit }, isCollapsed) {
      commit('updateIsRightMenuBottomCollapsed', isCollapsed)
    },
    setIsLayoutToggleInProgress({ commit }, inProgress) {
      commit('updateIsLayoutTableMapToggleInProgress', inProgress)
    },
    /**
     * Change layout programatically (Only for centralized/vuex layout modules: Location/Ecoconduite)
     */
    changeLayout({ commit, dispatch, state }, options = {}) {
      shouldLog &&
        console.log('changeLayout', {
          origin: options.origin || '?',
          hasPlugin: !!Vue.$layout,
          hasVm: !!Vue?.$layout?.vm,
          layoutType: state.layoutType,
          options,
        })
      if (Vue.$layout && state.layoutType === 'tlayout') {
        if (!Vue.$layout.vm) {
          return setTimeout(() => dispatch('changeLayout', options), 1000)
        }
        console.debugVerbose(8, 'app::changeLayout', options)
        options = normalizeOptions(options)
        Vue.$layout.vm.configure(options)
        if (options.components) {
          commit(
            'updateLayoutComponents',
            getComponentNames(options.components)
          )
          registerComponentsInLayoutPlugin(options.components)
        }
      } else {
        shouldLog &&
          console.warn('changeLayout with layoutType none has no effect')
      }
    },
    /**
     * Resets both store state and $layout (vue plugin, see TLayout.vue) state
     * @param {*} param0
     */
    resetLayout({ commit }) {
      commit('resetState')
      Vue.$layout.components = {}
      if (Vue.$layout.vm) {
        Vue.$layout.vm.configure(getLayoutDefaults())
      }
    },
    setRightMenuBottomCurrentHeight({ commit }, height) {
      commit('setRightMenuBottomCurrentHeight', height)
    },
  },
}

function normalizeOptions(options) {
  let components = options.components || {}
  Object.keys(options)
    .filter((k) => k !== 'components')
    .forEach((key) => {
      if (!['boolean', 'string'].includes(typeof options[key])) {
        components[key] = options[key]
        options[key] = true
      }
    })
  options.components = components
  return options
}

function getComponentNames(components) {
  return Object.keys(components)
    .filter((key) => typeof components[key] === 'string')
    .map((k) => components[k])
}

function registerComponentsInLayoutPlugin(components) {
  Object.keys(components)
    .filter((key) => typeof components[key] !== 'string')
    .map((key) => ({
      componentName: key,
      componentDefinition: components[key],
    }))
    .forEach(({ componentName, componentDefinition }) => {
      Vue.$layout.components[componentName] = componentDefinition
    })
}
